// Slice 2B1 · Canonical setup-progress resolver.
// Single source of truth for "where should this contractor land inside
// Set Up Your Business right now?". Consumed by:
//   • SetUpYourBusinessCard.jsx  (Dashboard "Resume Setup" CTA)
//   • SetUpYourBusiness.jsx      (route auto-resolve on /app/setup)
//   • Per-trade contextual actions on Step 1
// Reasoning is a pure function of the persisted backend state — never
// derived from localStorage draft, in-flight form state, or transient
// UI toggles.
//
// Rules (matching the QA correction spec):
//   1. Business & Trades incomplete → open Business & Trades.
//   2. Business & Trades complete + a selected trade is `in_progress`
//      → open that trade in Trade Setup (using saved trade order).
//   3. Multiple `in_progress` trades → open the first per saved order.
//   4. Trades marked `later` are deferred and DO NOT block progression.
//   5. All selected trades are `ready` or `later` → open the next
//      incomplete setup step (from state.next_step).

const STEP_ORDER = [
  "business_and_trades",
  "trade_setup",
  "documents_and_customer_defaults",
  "review_and_ready",
];

/**
 * Find the first trade in the persisted setup order whose stored status
 * is `in_progress`. Returns "" when none. `later` and `ready` are
 * treated as non-blocking. `Roofing` is the pilot trade and remains
 * `in_progress` until at least one of its systems is Ready — so the
 * queue treats it identically to any other `in_progress` trade.
 *
 * @param {Object} profile      - /company-profile shape
 * @returns {string} trade name (or "" if none in progress)
 */
export function firstInProgressTrade(profile) {
  const order = (profile?.trade_setup_order && profile.trade_setup_order.length)
    ? profile.trade_setup_order
    : (profile?.trade_categories || []);
  const status = profile?.trade_setup_status || {};
  for (const trade of order) {
    const s = (status[trade] || "in_progress").toLowerCase();
    if (s === "in_progress") return trade;
  }
  return "";
}

/**
 * Resolve the next destination for /app/setup based on the backend
 * onboarding state + persisted company profile.
 *
 * @param {Object} state    - GET /onboarding/set-up-your-business/state
 * @param {Object} profile  - GET /company-profile
 * @returns {{ step: string, trade: string, path: string }}
 *   step  — canonical step key
 *   trade — first in-progress trade name (only set when step===trade_setup)
 *   path  — fully-qualified `/app/setup?step=...&trade=...` URL
 */
export function resolveSetupDestination(state, profile) {
  const steps = state?.steps || [];
  const stepsByKey = {};
  for (const s of steps) stepsByKey[s.key] = s;

  // Rule 1: Step 1 incomplete → Business & Trades.
  if (!stepsByKey.business_and_trades?.complete) {
    return {
      step: "business_and_trades",
      trade: "",
      path: "/app/setup?step=business_and_trades",
    };
  }

  // Rule 2 + 3: Step 1 complete + a trade is in_progress → open that
  // trade in Trade Setup. Use the persisted saved order so ties are
  // deterministic and mirror the queue.
  if (!stepsByKey.trade_setup?.complete) {
    const trade = firstInProgressTrade(profile);
    const q = trade
      ? `?step=trade_setup&trade=${encodeURIComponent(trade)}`
      : "?step=trade_setup";
    return {
      step: "trade_setup",
      trade,
      path: `/app/setup${q}`,
    };
  }

  // Rule 5: Step 2 complete → jump to backend-declared next step
  // (documents_and_customer_defaults, then review_and_ready).
  const next = state?.next_step
    || STEP_ORDER.find((k) => !stepsByKey[k]?.complete)
    || "review_and_ready";
  // Slice 2C3 — when Step 3 is the destination AND the backend has
  // supplied per-family statuses, append the first unresolved family so
  // Resume Setup / Continue Setup lands on the correct family editor.
  // When the state does not (yet) carry family_statuses (e.g. legacy
  // callers, unit tests that predate 2C3), preserve the un-parameterized
  // Step 3 path from Slice 2B1 verbatim.
  if (next === "documents_and_customer_defaults"
      && stepsByKey.documents_and_customer_defaults?.family_statuses) {
    const step3 = stepsByKey.documents_and_customer_defaults;
    const fs = step3.family_statuses;
    const fv = step3.family_validation || {};
    const CANONICAL = ["proposal", "contract", "invoice", "customer_update", "closeout"];
    const firstUnresolved = CANONICAL.find(
      (f) => !(fs[f] === "ready" || fs[f] === "later"),
    ) || "proposal";
    // Default (Slice 2C3) — route to the first unresolved family.
    let family = firstUnresolved;
    let path = `/app/setup?step=${next}&family=${firstUnresolved}`;
    // Slice 2C4 — a validation-blocked family MUST take priority over
    // any unresolved family, because Ready / Later cannot be applied
    // until the unsafe wording is corrected.
    const firstBlocked = CANONICAL.find(
      (f) => fv[f] && Object.keys(fv[f]).length > 0,
    );
    if (firstBlocked) {
      family = firstBlocked;
      path = `/app/setup?step=${next}&family=${firstBlocked}`;
    }
    return { step: next, trade: "", family, path };
  }
  return {
    step: next,
    trade: "",
    path: `/app/setup?step=${next}`,
  };
}

export const SETUP_STEP_ORDER = STEP_ORDER;
